import { useEffect, useState, useCallback, useRef } from "react";
import cx from "classnames";

import {
  TransitionState,
  TransitionPhase,
  TransitionTimeouts,
  TransitionClassNames,
} from "./types";

type UseCssTransitionOptions = {
  /** Whether the component should be visible */
  visible: boolean;
  /** Duration in milliseconds for enter/exit transitions */
  timeout?: number;
  /** Different timeouts for different phases */
  timeouts?: TransitionTimeouts;
  /** Whether to mount/unmount on enter/exit */
  unmountOnExit?: boolean;
  /** Whether to show transition on first mount */
  appear?: boolean;
  /** Callback when transition enters */
  onEnter?: () => void;
  /** Callback when transition has entered */
  onEntered?: () => void;
  /** Callback when transition exits */
  onExit?: () => void;
  /** Callback when transition has exited */
  onExited?: () => void;
};

type UseCssTransitionResult = {
  /** Current transition state */
  state: TransitionState;
  /** Whether component should be rendered in DOM */
  shouldRender: boolean;
  /** CSS class names for current state */
  getClassNames: (baseClasses: TransitionClassNames) => string;
};

/**
 * Custom hook that replaces react-transition-group with React state and CSS Modules
 */
export const useCssTransition = (options: UseCssTransitionOptions): UseCssTransitionResult => {
  const {
    visible,
    timeout = 300,
    timeouts,
    unmountOnExit = false,
    appear = false,
    onEnter,
    onEntered,
    onExit,
    onExited,
  } = options;

  // Disable transitions in test environment (stable snapshots: no enter vs enterActive race).
  const isTestEnvironment = process.env.NODE_ENV === "test";

  const [state, setState] = useState<TransitionState>(() => {
    if (isTestEnvironment && visible && appear) {
      return TransitionState.ENTERED;
    }
    if (visible && appear) return TransitionState.ENTERING;
    return visible ? TransitionState.ENTERED : TransitionState.EXITED;
  });

  const [shouldRender, setShouldRender] = useState(() => {
    return visible || !unmountOnExit;
  });

  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isFirstMount = useRef(true);

  // When mounting with appear=true, state starts as ENTERING but handleEnter() does not run
  // (effect only runs when entering from EXITED/EXITING). Starting true ensures the first
  // paint is only `enter` so the next frame can apply `enterActive` and CSS transitions run.
  const [needsInitialState, setNeedsInitialState] = useState(() => {
    if (isTestEnvironment && visible && appear) {
      return false;
    }
    return Boolean(visible && appear);
  });

  // Get appropriate timeout for current transition
  const getTimeout = useCallback(
    (phase: TransitionPhase): number => {
      // In test environment, use 0ms timeout to skip animations
      if (isTestEnvironment) return 0;

      if (timeouts) {
        return timeouts[phase] ?? timeout;
      }
      return timeout;
    },
    [timeout, timeouts, isTestEnvironment]
  );

  // Clear existing timeout
  const clearExistingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  // Handle enter transition
  const handleEnter = useCallback(() => {
    clearExistingTimeout();

    if (unmountOnExit && !shouldRender) {
      setShouldRender(true);
    }

    if (isTestEnvironment) {
      setNeedsInitialState(false);
      setState(TransitionState.ENTERED);
      onEnter?.();
      onEntered?.();
      return;
    }

    // Set flag to apply initial state first for proper animation
    setNeedsInitialState(true);
    setState(TransitionState.ENTERING);
    onEnter?.();

    const enterTimeout =
      isFirstMount.current && appear
        ? getTimeout(TransitionPhase.APPEAR)
        : getTimeout(TransitionPhase.ENTER);

    if (enterTimeout > 0) {
      timeoutRef.current = setTimeout(() => {
        setState(TransitionState.ENTERED);
        onEntered?.();
      }, enterTimeout);
    } else {
      setState(TransitionState.ENTERED);
      onEntered?.();
    }
  }, [
    clearExistingTimeout,
    isTestEnvironment,
    unmountOnExit,
    shouldRender,
    onEnter,
    onEntered,
    appear,
    getTimeout,
  ]);

  // Handle exit transition
  const handleExit = useCallback(() => {
    clearExistingTimeout();

    setState(TransitionState.EXITING);
    onExit?.();

    const exitTimeout = getTimeout(TransitionPhase.EXIT);

    if (exitTimeout > 0) {
      timeoutRef.current = setTimeout(() => {
        setState(TransitionState.EXITED);
        onExited?.();

        if (unmountOnExit) {
          setShouldRender(false);
        }
      }, exitTimeout);
    } else {
      setState(TransitionState.EXITED);
      onExited?.();

      if (unmountOnExit) {
        setShouldRender(false);
      }
    }
  }, [clearExistingTimeout, onExit, onExited, getTimeout, unmountOnExit]);

  // Effect to handle visibility changes
  useEffect(() => {
    if (visible && (state === TransitionState.EXITED || state === TransitionState.EXITING)) {
      handleEnter();
    } else if (
      !visible &&
      (state === TransitionState.ENTERED || state === TransitionState.ENTERING)
    ) {
      handleExit();
    }

    if (isFirstMount.current) {
      isFirstMount.current = false;
    }
  }, [visible, state, handleEnter, handleExit]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearExistingTimeout();
    };
  }, [clearExistingTimeout]);

  // Generate CSS class names based on current state using cx
  const getClassNames = useCallback(
    (baseClasses: TransitionClassNames) => {
      switch (state) {
        case TransitionState.ENTERING:
          if (isTestEnvironment) {
            return cx(baseClasses.enterActive);
          }
          // Apply only initial state first, then transition to active state
          if (needsInitialState) {
            // Use requestAnimationFrame to apply enterActive class after initial render
            requestAnimationFrame(() => {
              setNeedsInitialState(false);
            });
            return cx(baseClasses.enter);
          }
          return cx(baseClasses.enter, baseClasses.enterActive);
        case TransitionState.ENTERED:
          return cx(baseClasses.enterActive);
        case TransitionState.EXITING:
          return cx(baseClasses.exit, baseClasses.exitActive);
        case TransitionState.EXITED:
          return cx(baseClasses.exit);
        default:
          return "";
      }
    },
    [state, needsInitialState, isTestEnvironment]
  );

  return {
    state,
    shouldRender,
    getClassNames,
  };
};
